import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { useLocationStore } from '@/features/location/store/location-store';
import type { LocationInformation } from '@/features/location/types/location-info.types';

interface CenterLocationMarkerProps {
  locationInfo: LocationInformation | undefined;
  visible: boolean;
}

/**
 * Marker that shows the user's location or searched address
 */
export function CenterLocationMarker({
  locationInfo,
  visible,
}: CenterLocationMarkerProps) {
  const currentPosition = useLocationStore((s) => s.currentPosition);

  // Only show when the user has explicitly enabled the location pin
  if (!visible) {
    return null;
  }

  // Priority:
  // 1. Show locationInfo coordinates (from search or reverse geocode)
  // 2. Fallback to currentPosition (user's GPS location)
  const coordinates = locationInfo?.coordinates || currentPosition;

  if (!coordinates) {
    return null;
  }

  const { lat, lng } = coordinates;

  return (
    <AdvancedMarker
      position={{ lat, lng }}
      zIndex={1000} // Show above charge point markers
    >
      <div className="relative">
        {/* Pulsing background effect */}
        <div className="absolute inset-0 bg-blue-500 rounded-full animate-ping opacity-75" />

        {/* Center pin icon */}
        <div className="relative bg-blue-600 rounded-full p-2 shadow-lg border-2 border-white">
          <svg
            className="w-5 h-5 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
        </div>
      </div>
    </AdvancedMarker>
  );
}
